import React from 'react';
import { DATA_SRC_TYPES } from '../utils/Constants';

const GOOGLE_MAPS_API_BASE_URL = 'https://maps.googleapis.com/maps/api/';

const GOOGLE_MAPS_SCRIPT_ID = '__GOOGLE_MAPS_API_SCRIPT_ID__';

const GoogleMapsApiKeyContext = React.createContext();

export function GoogleMapsApiKeyProvider({ apiKey, client, libraries, children }) {
	const [api, setApi] = React.useState(null);

	const keyRef = React.useRef(apiKey);
	const clientRef = React.useRef(client);
	const librariesRef = React.useRef(libraries);

	if (process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		React.useEffect(() => {
			if (apiKey !== keyRef.current) {
				console.error('Google Maps API key cannot change at run-time!');
			}

			if (client !== clientRef.current) {
				console.error(
					'Google Maps API client parameter cannot change at run-time!'
				);
			}
		}, [apiKey, client]);
	}

	React.useEffect(() => {
		const uuid = generateCallbackName();

		window[uuid] = (...args) => {
			console.log('[AXA Locator] Google Maps API loaded', {
				version: window.google?.maps?.version,
				apiKey: keyRef.current ? '***' : '(none)',
				client: clientRef.current || '(none)',
				scriptId: GOOGLE_MAPS_SCRIPT_ID
			});
			setApi(window.google);
			delete window[uuid];
		};

		const url = new URL('js', GOOGLE_MAPS_API_BASE_URL);

		url.searchParams.append('libraries', String(librariesRef.current));
		url.searchParams.append('callback', uuid);

		if (keyRef.current) {
			url.searchParams.append('key', keyRef.current);
		}

		if (clientRef.current) {
			url.searchParams.append('client', clientRef.current);
		}

		const script = document.createElement('script');

		script.src = String(url);
		script.id = GOOGLE_MAPS_SCRIPT_ID;

		if (!document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
			document.head.appendChild(script);
		}

		return () => {
			delete window[uuid];
		};
	}, []);

	return <GoogleMapsApiKeyContext.Provider children={children} value={api} />;
}

function generateCallbackName() {
	let uuid;

	do {
		uuid = 'CB_' + Math.round(Math.random() * Number.MAX_SAFE_INTEGER);
	} while (uuid in window);

	return uuid;
}

// Utility Hooks

export function useGoogleMapsApi() {
	return React.useContext(GoogleMapsApiKeyContext);
}

function useInstance(factory, inputs) {
	const [instance, setInstance] = React.useState(null);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	React.useEffect(() => {
		setInstance(() => factory());
	}, [...inputs]);

	return instance;
}

export function useFetchAutocompletePredictions() {
	const google = useGoogleMapsApi();

	return useInstance(() => {
		if (google == null) {
			return null;
		}

		const autocomplete = new google.maps.places.AutocompleteService();

		return function fetchAutocompletePredictions(request) {
			return placesApiCallToPromise(google, (callback) =>
				autocomplete.getPlacePredictions(request, callback)
			);
		};
	}, [google]);
}

export function useGoogleMap(lat, lng) {
	const google = useGoogleMapsApi();
	const [map, setMap] = React.useState();
	const [streetView, setStreetView] = React.useState();

	const mapId = React.useRef(
		'MAP_' + Math.round(Math.random() * Number.MAX_SAFE_INTEGER)
	);

	const mapContainer = document.getElementById(mapId.current);

	React.useEffect(() => {
		if (!google) return;

		const googleMapsOptions = {
			zoom: 11,
			center: new google.maps.LatLng(lat, lng),
			disableDefaultUI: true,
			noClear: true,
			mapTypeId: google.maps.MapTypeId.ROADMAP
		};

		if (!mapContainer) return;

		setMap(new google.maps.Map(mapContainer, googleMapsOptions));
	}, [google, mapContainer, lat, lng]);

	React.useEffect(() => {
		if (map == null) return;

		map.panTo(new google.maps.LatLng(lat, lng));
		map.setZoom(11);
	}, [google, map, lat, lng]);

	React.useEffect(() => {
		if (map == null) return;

		const streetViewOptions = {
			addressControl: false,
			enableCloseButton: false
		};

		const _streetView = map.getStreetView();
		_streetView.setOptions(streetViewOptions);
		setStreetView(_streetView);
	}, [map]);

	return [map, mapId.current, streetView];
}

export function useFetchPlaceDetails() {
	const google = useGoogleMapsApi();

	return useInstance(() => {
		if (google == null) {
			return null;
		}

		const places = new google.maps.places.PlacesService(
			// Facciamolo contento...
			document.createElement('div')
		);

		return function fetchPlaceDetails(request) {
			return placesApiCallToPromise(google, (callback) =>
				places.getDetails(request, callback)
			);
		};
	}, [google]);
}

export function useCalcRoute(googleMap, startAddress, calcRouteResult) {
	const [directionService, setDirectionService] = React.useState();
	const [directionRenderer, setDirectionRenderer] = React.useState();
	const [markerRouteStart, setMarkerRouteStart] = React.useState();
	const [markerRouteEnd, setMarkerRouteEnd] = React.useState();

	const google = useGoogleMapsApi();

	React.useEffect(() => {
		if (!google) return;

		setDirectionService(new google.maps.DirectionsService());

		setDirectionRenderer(
			new google.maps.DirectionsRenderer({
				suppressMarkers: true
			})
		);

		setMarkerRouteStart(
			new google.maps.Marker({
				icon: {
					url: require('../images/pin/pin_route_start.png'),
					size: new google.maps.Size(62, 80),
					scaledSize: new google.maps.Size(31, 40),
					origin: new google.maps.Point(0, 0),
					anchor: new google.maps.Point(15, 39)
				}
			})
		);

		setMarkerRouteEnd(
			new google.maps.Marker({
				icon: {
					url: require('../images/pin/pin_route_end.png'),
					size: new google.maps.Size(62, 80),
					scaledSize: new google.maps.Size(31, 40),
					origin: new google.maps.Point(0, 0),
					anchor: new google.maps.Point(15, 39)
				}
			})
		);
	}, [google]);

	function resetMap() {
		if (
			directionRenderer != null &&
			markerRouteStart != null &&
			markerRouteEnd != null &&
			googleMap != null
		) {
			directionRenderer.setMap(null);
			markerRouteStart.setMap(null);
			markerRouteEnd.setMap(null);
			googleMap.setZoom(11);
		}
	}

	return [
		useInstance(() => {
			if (!(google && googleMap && startAddress && calcRouteResult))
				return;

			const latlng = {
				lat: parseFloat(calcRouteResult.lat),
				lng: parseFloat(calcRouteResult.lng)
			};

			return function fetchCalcRoute(travelMode) {
				return directionsApiCallToPromise(
					google,
					googleMap,
					directionRenderer,
					markerRouteStart,
					markerRouteEnd,
					(callback) => {
						const geocoder = new google.maps.Geocoder();
						geocoder.geocode(
							{ location: latlng },
							(results, status) => {
								if (status === 'OK') {
									if (results[0]) {
										const endAddress =
											results[0].formatted_address;

										const travelModes = [
											google.maps.TravelMode.DRIVING,
											google.maps.TravelMode.TRANSIT,
											google.maps.TravelMode.WALKING
										];

										const request = {
											origin: startAddress,
											destination: endAddress,
											travelMode: travelModes[travelMode]
										};

										directionService.route(
											request,
											callback
										);
									}
								} else {
									console.error(
										'Geocoder failed due to: ' + status
									);
								}
							}
						);
					}
				);
			};
		}, [google, googleMap, startAddress, calcRouteResult]),
		resetMap
	];
}

export function useStreetView(streetView, streetViewResult, collateralEffects) {
	const google = useGoogleMapsApi();

	React.useEffect(() => {
		if (!(google && streetView && streetViewResult)) return;

		let isVisible = false;

		if (streetViewResult.lat && streetViewResult.lng) {
			streetView.setPosition(
				new google.maps.LatLng(
					streetViewResult.lat,
					streetViewResult.lng
				)
			);
			isVisible = true;
			collateralEffects();
		}
		streetView.setVisible(isVisible);
	}, [google, streetViewResult]);
}

function placesApiCallToPromise(google, factory) {
	return new Promise((resolve, reject) => {
		factory(function callback(result, status) {
			if (status !== google.maps.places.PlacesServiceStatus.OK) {
				reject(status);
			} else {
				resolve(result);
			}
		});
	});
}

function directionsApiCallToPromise(
	google,
	googleMap,
	directionRenderer,
	markerRouteStart,
	markerRouteEnd,
	factory
) {
	return new Promise((resolve, reject) => {
		factory(function callback(response, status) {
			if (status === google.maps.DirectionsStatus.OK) {
				const route = response.routes[0];
				const travelMode = response.request.travelMode;

				for (let i = 0; i < route.legs.length; i++) {
					const legs = route.legs[i];

					const calcRoute = {
						steps: legs.steps,
						duration: legs.duration.text,
						distance: legs.distance.text,
						show: () => {
							directionRenderer.setMap(googleMap);
							directionRenderer.setDirections(response);

							markerRouteStart.setPosition(legs.start_location);
							markerRouteStart.setTitle(legs.start_address);

							markerRouteEnd.setPosition(legs.end_location);
							markerRouteEnd.setTitle(legs.end_address);

							markerRouteStart.setMap(googleMap);
							markerRouteStart.setZIndex(1000);

							markerRouteEnd.setMap(googleMap);
							markerRouteEnd.setZIndex(1000);
						}
					};

					if (travelMode === google.maps.TravelMode.DRIVING) {
						calcRoute.show();
					}

					resolve(calcRoute);

					break;
				}
			} else {
				console.error('Error in directionService.route', status);
				reject(status);
			}
		});
	});
}

export function calcDistanceMatrixProcess(
	google,
	results,
	index,
	lat,
	lng,
	setSearchResults,
	setLoading,
	dataSrc
) {
	// Sbagliato perchè in caso di SHOW_ALL == true prende sempre l'ultima normalizeFnc...
	// La normalizeFnc dovrebbe dipendere dal result...
	// In searchProcess inserire una prop nei result con il nome del project/dataSrc
	try {
		if (results.length > 0)
			calcDistanceMatrix(
				google,
				results,
				index,
				lat,
				lng,
				setSearchResults,
				setLoading,
				(results) => {
					return results.map((result) =>
						dataSrc.normalizeResult(result)
					);
				},
				dataSrc
			);
		else {
			setSearchResults([]);
			setLoading(false);
		}
	} catch (e) {
		console.error(
			'Error in calcDistanceMatrix: fallback to plain normalizeResult',
			e.message
		);
		const normalizedResults = results.map((result) =>
			dataSrc.normalizeResult(result)
		);
		setSearchResults(normalizedResults);
		setLoading(false);
	}
}

export function calcDistanceMatrix(
	google,
	results,
	index,
	lat,
	lng,
	setSearchResults,
	setLoading,
	normalizeResults,
	dataSrc
) {
	try {
		const distanceMatrixService = new google.maps.DistanceMatrixService();

		index = index || 0;
		if (lat && lng) {
			const origins = [new google.maps.LatLng(lat, lng)];
			const destinations = [];
			const isArray = Array.isArray(results);

			let j = 0;

			let size = 0;

			if (isArray) {
				size = results.length;
			} else {
				size = results.size();
			}

			for (let i = index; i < size && j < 25; ++i) {
				j++;

				let tempObj = null;
				if (isArray) {
					tempObj = results[i];
				} else {
					tempObj = results.getAt(i);
				}

				let end = getResultLocation(dataSrc, tempObj);

				destinations.push(new google.maps.LatLng(end.lat(), end.lng()));
			}

			if (destinations.length > 0) {
				distanceMatrixService.getDistanceMatrix(
					{
						origins: origins,
						destinations: destinations,
						travelMode: google.maps.TravelMode.DRIVING
					},
					function callback(response, status) {
						if (status === google.maps.DistanceMatrixStatus.OK) {
							const elements = response.rows[0].elements;

							// lista titoli
							for (let i = 0; i < elements.length; ++i) {
								let obj = null;
								if (isArray) {
									obj = results[i + index];
								} else {
									obj = results.getAt(i + index);
								}

								let distance = '';

								if (elements[i].distance) {
									// distance matrix
									const tmp = elements[i].distance;
									distance = tmp.value;
								} else {
									// se non esiste, calcolo la distanza in linea d'aria
									let end = getResultLocation(dataSrc, obj);
									distance = calcDistance(end, lat, lng);
								}
								if (distance != null) {
									obj.driveDistance = distance;
								}
							}
						} else {
							for (let i = 0; i < size; ++i) {
								let obj;
								if (isArray) {
									obj = results[i + index];
								} else {
									obj = results.getAt(i + index);
								}
								if (obj) {
									let end = getResultLocation(dataSrc, obj);
									const distance = calcDistance(
										end,
										lat,
										lng
									);
									obj.driveDistance = distance;
								}
							}
							index = -25;
						}

						let newIndex = index + 25;
						if (newIndex > size - 1) {
							newIndex = size - 1 - newIndex;
						}

						if (newIndex > 0) {
							calcDistanceMatrix(
								google,
								results,
								newIndex,
								lat,
								lng,
								setSearchResults,
								setLoading,
								normalizeResults
							);
						} else {
							setSearchResults(normalizeResults(results));
							setLoading(false);
						}
					}
				);
			}
		}
	} catch (e) {
		throw e;
	}
}

function calcDistance(end, lat, lng) {
	try {
		const R = 6371; // earth's mean radius in km
		const dLat = toRad(end.lat() - lat);
		const dLong = toRad(end.lng() - lng);

		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRad(lat)) *
				Math.cos(toRad(end.lat())) *
				Math.sin(dLong / 2) *
				Math.sin(dLong / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const d = R * c; // in km
		const dM = parseFloat((d * 1000).toFixed(3)); // trasform in meters

		return dM;
	} catch (e) {}
}

function toRad(degree) {
	return (degree * Math.PI) / 180;
}

function getResultLocation(dataSrc, result) {
	let location;

	if (dataSrc && dataSrc.type === DATA_SRC_TYPES.URL) {
		location = dataSrc.getLocation(result);
	} else {
		location = result.getGeoElements
			? result.getGeoElements()[0].getOverlay()['position']
			: result.geometry.location;
	}

	return location;
}
